import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
    LayoutDashboard,
    Ticket,
    School,
    Building2,
    MoreHorizontal,
    UserCog,
    Tag,
    Layers,
    Network,
    Users,
    X,
    Mail,
    GraduationCap,
    BookOpenText,
    Settings
} from 'lucide-react';
import { useAuthStore } from '@/shared/stores/authStore';
import './BottomNav.css';

export const BottomNav: React.FC = () => {
    const { user, tenant } = useAuthStore();
    const location = useLocation();
    const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
    const slug = tenant?.slug || user?.school?.slug || 'school';

    const allNavItems = [
        // ── Super Admin ─────────────────────────────────────────────────────────
        { path: '/super-admin', label: 'Dashboard', icon: <LayoutDashboard size={22} />, roles: ['SUPER_ADMIN'] },
        { path: '/super-admin/tenants', label: 'Schools', icon: <School size={22} />, roles: ['SUPER_ADMIN'] },
        { path: '/super-admin/contacts', label: 'Contacts', icon: <Mail size={22} />, roles: ['SUPER_ADMIN'] },

        // ── School Admin (Primary in bottom bar: first 4 items) ──────────────────
        { path: `/${slug}/school-admin`, label: 'Dashboard', icon: <LayoutDashboard size={22} />, roles: ['SCHOOL_ADMIN'] },
        { path: `/${slug}/tickets`, label: 'Tickets', icon: <Ticket size={22} />, roles: ['SCHOOL_ADMIN'] },
        { path: `/${slug}/school-admin/students`, label: 'Students', icon: <GraduationCap size={22} />, roles: ['SCHOOL_ADMIN'] },
        { path: `/${slug}/school-admin/knowledge-hub`, label: 'Knowledge Hub', icon: <BookOpenText size={22} />, roles: ['SCHOOL_ADMIN'] },
        
        // ── School Admin (Items in "More" Menu) ──────────────────────────────────
        { path: `/${slug}/school-admin/departments`, label: 'Departments', icon: <Building2 size={22} />, roles: ['SCHOOL_ADMIN'] },
        { path: `/${slug}/school-admin/workflows`, label: 'Workflows', icon: <Network size={22} />, roles: ['SCHOOL_ADMIN'] },
        { path: `/${slug}/school-admin/employees`, label: 'Employees', icon: <UserCog size={22} />, roles: ['SCHOOL_ADMIN'] },
        { path: `/${slug}/school-admin/parents`, label: 'Parents', icon: <Users size={22} />, roles: ['SCHOOL_ADMIN'] },
        { path: `/${slug}/school-admin/tags`, label: 'Tags', icon: <Tag size={22} />, roles: ['SCHOOL_ADMIN'] },
        { path: `/${slug}/school-admin/classes`, label: 'Classes', icon: <Layers size={22} />, roles: ['SCHOOL_ADMIN'] },
        { path: `/${slug}/school-admin/settings`, label: 'School Settings', icon: <Settings size={22} />, roles: ['SCHOOL_ADMIN'] },
        { path: `/${slug}/school-admin/email-settings`, label: 'Email Settings', icon: <Mail size={22} />, roles: ['SCHOOL_ADMIN'] },

        // ── Employee ─────────────────────────────────────────────────────────────
        { path: '/employee', label: 'Workspace', icon: <LayoutDashboard size={22} />, roles: ['EMPLOYEE'] },
        { path: `/${slug}/tickets`, label: 'Tickets', icon: <Ticket size={22} />, roles: ['EMPLOYEE'] },
    ];

    const filteredItems = allNavItems.filter(item => user && item.roles.includes(user.role));

    // For Super Admin (3 items) & Employee (2 items), show all in bottom bar without More button
    // For School Admin (12 items), show first 4 in bottom bar and rest in More menu
    const isMoreNeeded = filteredItems.length > 4;
    const primaryNavItems = isMoreNeeded ? filteredItems.slice(0, 4) : filteredItems;
    const moreNavItems = isMoreNeeded ? filteredItems.slice(4) : [];

    // Check if the current route belongs to any item inside "More"
    const isMoreActive = moreNavItems.some(item => location.pathname === item.path || location.pathname.startsWith(item.path + '/'));

    const handleMoreClick = () => {
        setIsMoreMenuOpen(!isMoreMenuOpen);
    };

    return (
        <>
            {/* More Menu Overlay */}
            {isMoreMenuOpen && (
                <div
                    className="app-bottom-nav__menu-overlay"
                    onClick={() => setIsMoreMenuOpen(false)}
                >
                    <div
                        className="app-bottom-nav__menu-content"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="app-bottom-nav__menu-header">
                            <h3>More Options</h3>
                            <button onClick={() => setIsMoreMenuOpen(false)} aria-label="Close menu">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="app-bottom-nav__menu-grid">
                            {moreNavItems.map((item) => (
                                <NavLink
                                    key={item.path}
                                    to={item.path}
                                    onClick={() => setIsMoreMenuOpen(false)}
                                    className={({ isActive }) => `app-bottom-nav__menu-item ${isActive ? 'active' : ''}`}
                                >
                                    <div className="app-bottom-nav__menu-icon">
                                        {item.icon}
                                    </div>
                                    <span className="app-bottom-nav__menu-label">{item.label}</span>
                                </NavLink>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            <nav className="app-bottom-nav lg:hidden">
                {primaryNavItems.map((item) => (
                    <NavLink
                        key={item.path}
                        to={item.path}
                        end={item.path === `/${slug}/school-admin` || item.path === '/super-admin' || item.path === '/employee'}
                        className={({ isActive }) => `app-bottom-nav__item ${isActive ? 'active' : ''}`}
                    >
                        {item.icon}
                        <span className="app-bottom-nav__label">{item.label}</span>
                    </NavLink>
                ))}

                {/* More Button */}
                {isMoreNeeded && (
                    <button
                        className={`app-bottom-nav__item ${isMoreMenuOpen || isMoreActive ? 'active' : ''}`}
                        onClick={handleMoreClick}
                        type="button"
                    >
                        <MoreHorizontal size={22} />
                        <span className="app-bottom-nav__label">More</span>
                    </button>
                )}
            </nav>
        </>
    );
};
